import { useQuery } from "@tanstack/react-query";
import { CORE_COVERAGE_TYPES, CORE_COVERAGE_LABELS } from "@shared/constants/coverageLines";
import { useState, useMemo, useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarDays,
  ExternalLink,
  Search,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  Zap,
  BarChart2,
  Mail,
  PhoneCall,
  FileText,
  Star,
  ChevronRight,
  ShieldAlert,
  Layers,
  Filter,
  LayoutGrid,
  List,
  Pencil,
  Target,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import {
  addMonths,
  addDays,
  format,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
} from "date-fns";
import type { ClientPlan, Client, Plan, Enrollment } from "@shared/schema";
import { calendarDaysUntil, countClientsRenewingWithin, resolvePlanRenewalDate } from "@shared/utils/renewals";
import { buildBookPremiumContext, clientPlanPremiumKey, monthlyPremiumByClient, monthlyPremiumByClientPlan } from "@shared/utils/bookPremium";

type EnrichedClientPlan = ClientPlan & {
  plan?: Partial<Plan> | null;
  totalPremium: string | number;
  status?: string | null;
};

const PLAN_TYPE_STYLES: Record<string, { bg: string; text: string; label: string; dot: string }> = {
  health:     { bg: "bg-blue-100",   text: "text-blue-800",   label: "Medical",    dot: "bg-blue-500" },
  medical:    { bg: "bg-blue-100",   text: "text-blue-800",   label: "Medical",    dot: "bg-blue-500" },
  dental:     { bg: "bg-green-100",  text: "text-green-800",  label: "Dental",     dot: "bg-green-500" },
  vision:     { bg: "bg-purple-100", text: "text-purple-800", label: "Vision",     dot: "bg-purple-500" },
  life:       { bg: "bg-indigo-100", text: "text-indigo-800", label: "Life",       dot: "bg-indigo-500" },
  disability: { bg: "bg-orange-100", text: "text-orange-800", label: "Disability", dot: "bg-orange-500" },
  voluntary:  { bg: "bg-pink-100",   text: "text-pink-800",   label: "Voluntary",  dot: "bg-pink-500" },
};

const RENEWAL_STAGES = [
  { key: "not_started",  label: "Not Started",       color: "text-gray-500",  bg: "bg-gray-100",   border: "border-gray-200" },
  { key: "rfp_out",      label: "RFP Out",           color: "text-blue-600",  bg: "bg-blue-50",    border: "border-blue-200" },
  { key: "proposals_in",      label: "Proposals In",       color: "text-indigo-600",  bg: "bg-indigo-50",   border: "border-indigo-200" },
  { key: "presented",         label: "Presented",          color: "text-amber-600",   bg: "bg-amber-50",    border: "border-amber-200" },
  { key: "uw_review",         label: "UW Review",          color: "text-orange-600",  bg: "bg-orange-50",   border: "border-orange-200" },
  { key: "open_enrollment",   label: "In Open Enrollment", color: "text-purple-600",  bg: "bg-purple-50",   border: "border-purple-200" },
  { key: "complete",          label: "Complete",           color: "text-green-600",   bg: "bg-green-50",    border: "border-green-200" },
];


function PlanTypeBadge({ type }: { type: string }) {
  const key = type.toLowerCase() === "medical" ? "health" : type.toLowerCase();
  const style = PLAN_TYPE_STYLES[key] ?? { bg: "bg-gray-100", text: "text-gray-700", label: type, dot: "bg-gray-400" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

type UrgencyLevel = "critical" | "urgent" | "planning" | "future" | "overdue" | "no_date";

function getUrgency(days: number): UrgencyLevel {
  if (days < 0) return "overdue";
  if (days <= 30) return "critical";
  if (days <= 60) return "urgent";
  if (days <= 90) return "planning";
  return "future";
}

const URGENCY_CONFIG: Record<UrgencyLevel, { label: string; color: string; bg: string; border: string; dot: string }> = {
  overdue:  { label: "Overdue",   color: "text-red-900",    bg: "bg-red-100",    border: "border-red-400",    dot: "bg-red-700" },
  critical: { label: "Critical",  color: "text-red-700",    bg: "bg-red-50",     border: "border-red-200",    dot: "bg-red-500" },
  urgent:   { label: "Urgent",    color: "text-amber-700",  bg: "bg-amber-50",   border: "border-amber-200",  dot: "bg-amber-500" },
  planning: { label: "Planning",  color: "text-blue-700",   bg: "bg-blue-50",    border: "border-blue-200",   dot: "bg-blue-400" },
  future:   { label: "Future",    color: "text-gray-600",   bg: "bg-gray-50",    border: "border-gray-200",   dot: "bg-gray-400" },
  no_date:  { label: "No Date",   color: "text-gray-400",   bg: "bg-gray-50",    border: "border-gray-200",   dot: "bg-gray-300" },
};

function UrgencyBadge({ days, noDate, isComplete }: { days: number; noDate?: boolean; isComplete?: boolean }) {
  if (noDate) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-400 border border-dashed border-gray-300">
        — No date set
      </span>
    );
  }
  // Stage "complete" already renewed the plan — an overdue/critical badge here reads as a false alarm
  if (isComplete) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
        Renewed
      </span>
    );
  }
  const level = getUrgency(days);
  const cfg = URGENCY_CONFIG[level];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {level === "overdue" ? `${Math.abs(days)}d overdue` : cfg.label}
    </span>
  );
}

function StageBadge({ stageKey }: { stageKey: string }) {
  const stage = RENEWAL_STAGES.find(s => s.key === stageKey) || RENEWAL_STAGES[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${stage.bg} ${stage.color} border ${stage.border}`}>
      {stage.label}
    </span>
  );
}

type RenewalRecord = {
  clientId: number;
  clientName: string;
  earliestRenewalDate: Date | null;
  planTypes: string[];
  totalMonthlyPremium: number;
  daysUntil: number;
  urgency: UrgencyLevel;
  planCount: number;
  noDate: boolean;
};

type OpportunityRecord = {
  clientId: number;
  clientName: string;
  hasTypes: string[];
  missingTypes: string[];
  currentMonthlyPremium: number;
};

function OEDateCell({
  clientId,
  oeData,
  renewalDate,
}: {
  clientId: number;
  oeData: { startDate?: string | null; endDate?: string | null } | null | undefined;
  renewalDate: Date | null;
}) {
  const { toast } = useToast();
  const start = oeData?.startDate || "";
  const end = oeData?.endDate || "";
  const [draft, setDraft] = useState({ start, end });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep draft in sync when API data loads (only when popover is closed)
  useEffect(() => {
    if (!open) {
      setDraft({ start, end });
    }
  }, [start, end, open]);

  const displayStart = draft.start ? format(new Date(draft.start + "T00:00:00"), "MMM d") : null;
  const displayEnd = draft.end ? format(new Date(draft.end + "T00:00:00"), "MMM d, yyyy") : null;

  const dateError = (() => {
    if (!draft.start || !draft.end) return null;
    if (draft.end < draft.start) return "End date can't be before the start date.";
    if (renewalDate && draft.end > format(renewalDate, "yyyy-MM-dd")) {
      return `Must end by the renewal date (${format(renewalDate, "MMM d, yyyy")}) — enrollment closes before the new plan year starts.`;
    }
    return null;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiRequest("PUT", `/api/clients/${clientId}/open-enrollment`, {
        startDate: draft.start,
        endDate: draft.end,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        toast({
          title: "Could not save open enrollment dates",
          description: body?.message || "Please try again.",
          variant: "destructive",
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/open-enrollments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "open-enrollment"] });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 text-sm text-left group">
          {displayStart && displayEnd ? (
            <span className="text-gray-700 dark:text-gray-300">
              {displayStart} – {displayEnd}
            </span>
          ) : displayStart ? (
            <span className="text-gray-700 dark:text-gray-300">{displayStart} –</span>
          ) : (
            <span className="text-gray-400 italic">Set dates</span>
          )}
          <Pencil className="h-3 w-3 text-gray-300 group-hover:text-blue-500 shrink-0 transition-colors" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Open Enrollment Period</p>
        <p className="text-xs text-gray-400 mb-2">Synced with Client → Benefits → Open Enrollment</p>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Enrollment Start</label>
            <Input
              type="date"
              className="h-8 text-sm"
              value={draft.start}
              onChange={e => setDraft(d => ({ ...d, start: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Enrollment Deadline</label>
            <Input
              type="date"
              className="h-8 text-sm"
              value={draft.end}
              onChange={e => setDraft(d => ({ ...d, end: e.target.value }))}
            />
          </div>
          {dateError && (
            <p className="text-xs text-red-600">{dateError}</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs flex-1" onClick={handleSave} disabled={saving || !draft.start || !draft.end || !!dateError}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function OEProgressCell({ progress }: { progress: { completed: number; total: number } | null | undefined }) {
  if (!progress || progress.total === 0) {
    return <span className="text-xs text-gray-400 italic">—</span>;
  }
  const pct = Math.round((progress.completed / progress.total) * 100);
  const color = pct === 100
    ? "text-green-600 dark:text-green-400"
    : pct >= 50
      ? "text-amber-600 dark:text-amber-400"
      : "text-gray-600 dark:text-gray-400";
  return (
    <span className={`text-sm font-medium ${color}`}>
      {progress.completed}/{progress.total}
      <span className="text-xs font-normal text-gray-400 ml-1">({pct}%)</span>
    </span>
  );
}

export default function Renewals() {
  const today = new Date();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterUrgency, setFilterUrgency] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"table" | "pipeline">("table");
  const [stageOverrides, setStageOverrides] = useState<Record<number, string>>({});

  const { data: clientPlans = [], isLoading: cpLoading } = useQuery<EnrichedClientPlan[]>({ queryKey: ["/api/clientplans"] });
  const { data: clients = [], isLoading: clientsLoading } = useQuery<Client[]>({ queryKey: ["/api/clients"] });
  const { data: plans = [], isLoading: plansLoading } = useQuery<Plan[]>({ queryKey: ["/api/plans"] });
  const { data: oeWindows = [] } = useQuery<Array<{ clientId: number; startDate: string | null; endDate: string | null }>>({ queryKey: ["/api/open-enrollments"] });
  const { data: oeProgress = [] } = useQuery<Array<{ clientId: number; completed: number; total: number }>>({ queryKey: ["/api/open-enrollments/progress"] });
  // Premium comes from enrollments (same source as Reports), not client_plans.total_premium (DEV-327)
  const { data: enrollments = [], isLoading: enrollmentsLoading } = useQuery<Enrollment[]>({ queryKey: ["/api/enrollments"] });

  const isLoading = cpLoading || clientsLoading || plansLoading || enrollmentsLoading;
  const plansArray = Array.isArray(plans) ? plans : [];
  const clientsArray = Array.isArray(clients) ? clients : [];
  // active-only filter lives here so it covers every cpArray consumer below
  const activeClientIds = new Set(
    clientsArray.filter(c => (c.status || "").toLowerCase() === "active").map(c => c.id)
  );
  const cpArray = (Array.isArray(clientPlans) ? clientPlans : []).filter(cp => activeClientIds.has(cp.clientId));

  const getClientName = (id: number) => clientsArray.find(c => c.id === id)?.name || "Unknown";
  const getPlanById = (id: number) => plansArray.find(p => p.id === id) || null;

  // Date-only (local midnight) resolution shared with Dashboard/Reports/Calendar
  const resolveRenewalDate = (cp: EnrichedClientPlan) =>
    resolvePlanRenewalDate(cp, getPlanById);

  const resolvePlanType = (cp: EnrichedClientPlan) => {
    const fb = getPlanById(cp.planId);
    const raw = ((cp.plan?.type || fb?.type || "health") as string).toLowerCase();
    return raw === "medical" ? "health" : raw;
  };

  const bookCtx = useMemo(
    () => buildBookPremiumContext({ clients: clientsArray, plans: plansArray as any[] }),
    [clients, plans],
  );
  const premiumByClient = useMemo(() => monthlyPremiumByClient(enrollments, bookCtx), [enrollments, bookCtx]);
  const premiumByClientPlan = useMemo(() => monthlyPremiumByClientPlan(enrollments, bookCtx), [enrollments, bookCtx]);

  const lookAhead = addMonths(today, 12);
  // Keep renewals visible through the end of their renewal month — they drop
  // off on the 1st of the following month, regardless of stage.
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const oeMap = useMemo(() => {
    const m = new Map<number, { startDate: string | null; endDate: string | null }>();
    oeWindows.forEach(w => m.set(w.clientId, { startDate: w.startDate, endDate: w.endDate }));
    return m;
  }, [oeWindows]);

  const oeProgressMap = useMemo(() => {
    const m = new Map<number, { completed: number; total: number }>();
    oeProgress.forEach(p => m.set(p.clientId, { completed: p.completed, total: p.total }));
    return m;
  }, [oeProgress]);

  const allRenewals: RenewalRecord[] = useMemo(() => {
    // Aggregate all data per client across all their plans
    const byClient = new Map<number, { dates: Date[]; types: Set<string>; planCount: number }>();

    cpArray.forEach(cp => {
      const cid = cp.clientId;
      if (!byClient.has(cid)) byClient.set(cid, { dates: [], types: new Set(), planCount: 0 });
      const entry = byClient.get(cid)!;
      const rd = resolveRenewalDate(cp);
      if (rd) entry.dates.push(rd);
      entry.types.add(resolvePlanType(cp));
      entry.planCount++;
    });

    const records: RenewalRecord[] = [];

    byClient.forEach((entry, clientId) => {
      const planTypes = Array.from(entry.types);
      const premium = premiumByClient.get(clientId) ?? 0;
      const planCount = entry.planCount;

      // Window: first of current month → next 12 months. Renewals stay visible
      // through the end of their renewal month (so "Complete" clients are still
      // in view until the cycle rolls over), then drop off on the 1st.
      const windowDates = entry.dates.filter(d => d >= currentMonthStart && d <= lookAhead);

      if (windowDates.length === 0 && entry.dates.length === 0) {
        // No renewal date on any plan — include as "no date set"
        records.push({
          clientId,
          clientName: getClientName(clientId),
          earliestRenewalDate: null,
          planTypes,
          totalMonthlyPremium: premium,
          daysUntil: 9999,
          urgency: "no_date",
          planCount,
          noDate: true,
        });
        return;
      }

      if (windowDates.length === 0) return; // dates exist but all outside window (far future/far past)

      const earliest = windowDates.reduce((a, b) => a < b ? a : b);
      // Calendar days — differenceInDays against "now" (not midnight) undercounts by one
      const days = calendarDaysUntil(earliest, today);
      records.push({
        clientId,
        clientName: getClientName(clientId),
        earliestRenewalDate: earliest,
        planTypes,
        totalMonthlyPremium: premium,
        daysUntil: days,
        urgency: getUrgency(days),
        planCount,
        noDate: false,
      });
    });

    return records.sort((a, b) => {
      if (a.noDate && !b.noDate) return 1;
      if (!a.noDate && b.noDate) return -1;
      if (!a.earliestRenewalDate) return 1;
      if (!b.earliestRenewalDate) return -1;
      return a.earliestRenewalDate.getTime() - b.earliestRenewalDate.getTime();
    });
  }, [cpArray, clientsArray, plansArray, premiumByClient]);

  const serverStages = useMemo(() => {
    const map = new Map<number, string>();
    clientsArray.forEach(client => {
      const stage = (client as any).renewalStage;
      if (stage) map.set(client.id, stage);
    });
    return map;
  }, [clientsArray]);

  const setStage = async (clientId: number, stage: string) => {
    setStageOverrides(prev => ({ ...prev, [clientId]: stage }));
    const response = await apiRequest("PATCH", `/api/clients/${clientId}`, { renewalStage: stage });
    if (!response.ok) {
      setStageOverrides(prev => {
        const { [clientId]: _dropped, ...rest } = prev;
        return rest;
      });
      const body = await response.json().catch(() => null);
      toast({
        title: "Could not update renewal stage",
        description: body?.message || "Please try again.",
        variant: "destructive",
      });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
  };

  const getStage = (clientId: number) =>
    stageOverrides[clientId] || serverStages.get(clientId) || "not_started";

  const [noticeTarget, setNoticeTarget] = useState<RenewalRecord | null>(null);
  const [noticeSubject, setNoticeSubject] = useState("");
  const [noticeMessage, setNoticeMessage] = useState("");
  const [noticeSending, setNoticeSending] = useState(false);
  const noticeRecipient = noticeTarget
    ? (clientsArray.find(c => c.id === noticeTarget.clientId) as any)?.contactEmail || null
    : null;

  const openRenewalNotice = (renewal: RenewalRecord) => {
    const renewalDateText = renewal.earliestRenewalDate
      ? format(renewal.earliestRenewalDate, "MMMM d, yyyy")
      : null;
    const coverageText = renewal.planTypes
      .map(t => (PLAN_TYPE_STYLES[t]?.label || t).toLowerCase())
      .join(", ");
    setNoticeSubject(
      renewalDateText
        ? `Your benefits renewal — ${format(renewal.earliestRenewalDate!, "MMMM yyyy")}`
        : "Your upcoming benefits renewal"
    );
    setNoticeMessage(
      `Hi ${renewal.clientName} team,\n\n` +
      `Your ${coverageText} coverage ${renewalDateText ? `is scheduled to renew on ${renewalDateText}` : "is coming up for renewal"}. ` +
      `Now is a good time to review your current plans and consider any changes for the new plan year.\n\n` +
      `We'll reach out shortly to schedule a renewal review. If you have any questions in the meantime, just reply to this email.`
    );
    setNoticeTarget(renewal);
  };

  const handleSendNotice = async () => {
    if (!noticeTarget) return;
    setNoticeSending(true);
    try {
      const response = await apiRequest("POST", `/api/clients/${noticeTarget.clientId}/renewal-notice`, {
        subject: noticeSubject,
        message: noticeMessage,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        toast({
          title: "Could not send renewal notice",
          description: body?.message || "Please try again.",
          variant: "destructive",
        });
        return;
      }
      const body = await response.json().catch(() => null);
      toast({
        title: "Renewal notice sent",
        description: `Emailed ${body?.sentTo || "the client contact"} and notified the employer portal.`,
      });
      setNoticeTarget(null);
    } finally {
      setNoticeSending(false);
    }
  };

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = addMonths(today, i);
      return { value: format(d, "yyyy-MM"), label: format(d, "MMMM"), date: d };
    });
  }, []);

  const filteredRenewals = useMemo(() => {
    return allRenewals.filter(r => {
      if (search && !r.clientName.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterType !== "all" && !r.planTypes.includes(filterType)) return false;
      if (filterUrgency !== "all" && r.urgency !== filterUrgency) return false;
      if (filterMonth !== "all") {
        if (!r.earliestRenewalDate) return false;
        // Compare "yyyy-MM" keys — parsing filterMonth via new Date() reads it
        // as UTC midnight, which shifts a month back in negative-offset zones.
        if (format(r.earliestRenewalDate, "yyyy-MM") !== filterMonth) return false;
      }
      return true;
    });
  }, [allRenewals, search, filterType, filterUrgency, filterMonth]);

  const calendarData = useMemo(() => {
    return monthOptions.map(mo => {
      const start = startOfMonth(mo.date);
      const end = endOfMonth(mo.date);
      const monthRenewals = allRenewals.filter(r => r.earliestRenewalDate && isWithinInterval(r.earliestRenewalDate, { start, end }));
      const typeCount: Record<string, number> = {};
      monthRenewals.forEach(r => r.planTypes.forEach(t => { typeCount[t] = (typeCount[t] || 0) + 1; }));
      const stageCount: Record<string, number> = {};
      monthRenewals.forEach(r => {
        const key = getStage(r.clientId);
        stageCount[key] = (stageCount[key] || 0) + 1;
      });
      return {
        ...mo,
        count: monthRenewals.length,
        typeCount,
        stageCount,
        premium: monthRenewals.reduce((s, r) => s + r.totalMonthlyPremium, 0),
      };
    });
  }, [allRenewals, monthOptions, stageOverrides, serverStages]);

  // Map stage keys → Tailwind bar background colors for the monthly breakdown
  const STAGE_BAR_COLORS: Record<string, string> = {
    not_started:     "bg-gray-300 dark:bg-gray-600",
    rfp_out:         "bg-blue-500",
    proposals_in:    "bg-indigo-500",
    presented:       "bg-amber-500",
    uw_review:       "bg-orange-500",
    open_enrollment: "bg-purple-500",
    complete:        "bg-green-500",
  };

  // Carrier concentration — across ALL client-plans (not just upcoming renewals)
  const carrierConcentration = useMemo(() => {
    const map = new Map<string, { name: string; count: number; premium: number }>();
    // A client can hold several links (plan years) to one plan — count its premium once
    const premiumCounted = new Set<string>();
    cpArray.forEach(cp => {
      // carrier name is on cp.plan.carrier (the joined field from storage)
      const carrierName = (cp.plan as any)?.carrier || "Unknown Carrier";
      const carrierId = String((cp.plan as any)?.carrierId || carrierName);
      const existing = map.get(carrierId) || { name: carrierName, count: 0, premium: 0 };
      const key = clientPlanPremiumKey(cp.clientId, cp.planId);
      const premium = premiumCounted.has(key) ? 0 : (premiumByClientPlan.get(key) ?? 0);
      premiumCounted.add(key);
      map.set(carrierId, { name: carrierName, count: existing.count + 1, premium: existing.premium + premium });
    });
    const total = cpArray.length || 1;
    return Array.from(map.values())
      .map(c => ({ ...c, pct: Math.round((c.count / total) * 100) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [cpArray, premiumByClientPlan]);

  // Opportunities — check ALL client-plans regardless of renewal date
  const opportunities: OpportunityRecord[] = useMemo(() => {
    const typesByClient = new Map<number, Set<string>>();

    cpArray.forEach(cp => {
      if (!typesByClient.has(cp.clientId)) typesByClient.set(cp.clientId, new Set());
      typesByClient.get(cp.clientId)!.add(resolvePlanType(cp));
    });

    // Which lines count as a "gap" lives in shared/constants/coverageLines.ts.
    const core = CORE_COVERAGE_TYPES;
    return Array.from(typesByClient.entries())
      .map(([clientId, types]) => {
        const missing = core.filter(t => !types.has(t));
        return {
          clientId,
          clientName: getClientName(clientId),
          hasTypes: Array.from(types),
          missingTypes: missing,
          currentMonthlyPremium: premiumByClient.get(clientId) || 0,
        };
      })
      .filter(o => o.missingTypes.length > 0 && o.missingTypes.length < core.length)
      .sort((a, b) =>
        a.missingTypes.length - b.missingTypes.length ||
        b.currentMonthlyPremium - a.currentMonthlyPremium
      );
  }, [cpArray, clientsArray, premiumByClient]);

  const critical = allRenewals.filter(r => r.urgency === "critical");
  const urgent = allRenewals.filter(r => r.urgency === "urgent");
  const premiumRenewing90d = allRenewals.filter(r => r.daysUntil <= 90).reduce((s, r) => s + r.totalMonthlyPremium, 0);
  const totalBookMonthlyPremium = allRenewals.reduce((s, r) => s + r.totalMonthlyPremium, 0);
  const largestRenewal = [...allRenewals].sort((a, b) => b.totalMonthlyPremium - a.totalMonthlyPremium)[0];
  // Distinct clients renewing today..+90 — same count as the Dashboard card.
  // (activePipeline below also keeps this month's overdue renewals for the stage bars.)
  const renewingIn90Count = countClientsRenewingWithin(cpArray, 90, { getPlan: getPlanById });

  // Active pipeline = renewals from the start of the current month through
  // the next ~90 days. Anything further out isn't actionable yet and would
  // just inflate the "Not Started" bar with noise.
  const activePipeline = useMemo(() => {
    return allRenewals.filter(r => r.daysUntil <= 90);
  }, [allRenewals]);

  const stageDistribution = useMemo(() => {
    return RENEWAL_STAGES.map(s => ({
      ...s,
      count: activePipeline.filter(r => getStage(r.clientId) === s.key).length,
      premium: activePipeline
        .filter(r => getStage(r.clientId) === s.key)
        .reduce((sum, r) => sum + r.totalMonthlyPremium, 0),
    }));
  }, [activePipeline, stageOverrides, serverStages]);

  const premiumByUrgency = useMemo(() => {
    const tiers: { label: string; urgency: UrgencyLevel; bar: string; text: string }[] = [
      { label: "Overdue",           urgency: "overdue",  bar: "bg-red-700",   text: "text-red-800"   },
      { label: "Critical (<30d)",   urgency: "critical", bar: "bg-red-500",   text: "text-red-600"   },
      { label: "Urgent (31–60d)",   urgency: "urgent",   bar: "bg-amber-400", text: "text-amber-600" },
      { label: "Planning (61–90d)", urgency: "planning", bar: "bg-blue-400",  text: "text-blue-600"  },
      { label: "Future (>90d)",     urgency: "future",   bar: "bg-gray-300",  text: "text-gray-400"  },
    ];
    const totalPremium = allRenewals.reduce((s, r) => s + r.totalMonthlyPremium, 0) || 1;
    return tiers.map(t => {
      const subset = allRenewals.filter(r => r.urgency === t.urgency);
      const premium = subset.reduce((s, r) => s + r.totalMonthlyPremium, 0);
      return { ...t, count: subset.length, premium, pct: Math.round((premium / totalPremium) * 100) };
    });
  }, [allRenewals]);

  const notStartedAtRisk = useMemo(() => {
    return allRenewals.filter(r => getStage(r.clientId) === "not_started" && r.daysUntil <= 60);
  }, [allRenewals, stageOverrides, serverStages]);

  const crossSellByType = useMemo(() => {
    const missing: Record<string, number> = {};
    opportunities.forEach(o => {
      o.missingTypes.forEach(t => {
        missing[t] = (missing[t] || 0) + 1;
      });
    });
    return Object.entries(missing)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([type, count]) => ({ type, count }));
  }, [opportunities]);

  const quickWins = useMemo(() => {
    return opportunities.filter(o => o.missingTypes.length === 1);
  }, [opportunities]);

  const coverageCompleteness = useMemo(() => {
    const core = CORE_COVERAGE_TYPES;
    const dist: Record<number, number> = {};
    opportunities.forEach(o => {
      const have = core.filter(t => o.hasTypes.includes(t)).length;
      dist[have] = (dist[have] || 0) + 1;
    });
    // 1 .. (N-1) lines — a client with all N lines isn't an opportunity.
    return Array.from({ length: core.length - 1 }, (_, i) => i + 1).map(lines => ({
      lines,
      label: `${lines} of ${core.length}`,
      count: dist[lines] || 0,
    }));
  }, [opportunities]);

  const fmt$ = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-64" />
        <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded-lg" />)}</div>
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb — this page is only reachable from Clients / dashboard
          links, so give it an explicit way back. */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Link href="/clients" className="hover:text-foreground hover:underline">
          Clients
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium">Renewals</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-blue-600" />
            Renewal Management
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            <span data-testid="renewals-90-count">{renewingIn90Count}</span> renewal{renewingIn90Count !== 1 ? "s" : ""} in the next 90 days
            {premiumRenewing90d > 0 && (
              <span className="ml-2 text-green-600 font-medium">· {fmt$(premiumRenewing90d)}/mo premium renewing in 90 days</span>
            )}
          </p>
        </div>
      </div>

      {/* Needs Attention Banner */}
      {(critical.length > 0 || urgent.length > 0) && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800 p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-red-800 dark:text-red-200 text-sm">
                Needs Immediate Attention
              </p>
              <p className="text-sm text-red-600 dark:text-red-300 mt-0.5">
                {critical.length > 0 && (
                  <><strong>{critical.length}</strong> renewal{critical.length !== 1 ? "s" : ""} due within 30 days</>
                )}
                {critical.length > 0 && urgent.length > 0 && " · "}
                {urgent.length > 0 && (
                  <><strong>{urgent.length}</strong> renewal{urgent.length !== 1 ? "s" : ""} due within 60 days</>
                )}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {critical.map(r => (
                  <Link key={r.clientId} href={`/clients/${r.clientId}`}>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium border border-red-200 hover:bg-red-200 cursor-pointer">
                      {r.clientName}
                      <ChevronRight className="h-3 w-3" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Critical (&lt;30d)</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">{critical.length}</p>
            <p className="text-xs text-red-500 mt-0.5 font-medium">
              {fmt$(critical.reduce((s, r) => s + r.totalMonthlyPremium, 0))}/mo premium at risk
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-400">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Due in 90 Days</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1" data-testid="renewals-due-90-card">
              {renewingIn90Count}
            </p>
            <p className="text-xs text-amber-600 mt-0.5 font-medium">{fmt$(premiumRenewing90d)}/mo premium</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-400">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Monthly Premium (12mo book)</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {fmt$(totalBookMonthlyPremium)}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">across {allRenewals.length} renewing client{allRenewals.length !== 1 ? "s" : ""}</p>
            <p className="text-xs text-gray-400 mt-1" data-testid="text-renewals-reports-note">
              Reports also counts onboarding groups' live enrollments, so its total can be higher.
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-400">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Cross-sell Openings</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {opportunities.length}
            </p>
            <p className="text-xs text-blue-500 mt-0.5 font-medium">{quickWins.length} missing just 1 line</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="renewals">
        <TabsList className="mb-4">
          <TabsTrigger value="renewals">Renewals</TabsTrigger>
          <TabsTrigger value="opportunities">
            Opportunities
            {opportunities.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                {opportunities.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* ── RENEWALS TAB ── */}
        <TabsContent value="renewals" className="space-y-4 mt-0">
          {/* 12-Month Calendar Grid */}
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
                {calendarData.map(mo => (
                  <button
                    key={mo.value}
                    onClick={() => setFilterMonth(filterMonth === mo.value ? "all" : mo.value)}
                    className={`rounded-lg border p-2 text-left transition-all cursor-pointer hover:border-blue-400 hover:shadow-sm ${
                      filterMonth === mo.value
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950 shadow-sm"
                        : mo.count > 0
                        ? "border-gray-200 dark:border-gray-700"
                        : "border-dashed border-gray-200 dark:border-gray-700 opacity-50"
                    }`}
                  >
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide leading-none">{format(mo.date, "MMM")}</p>
                    <p className="text-xs text-gray-400 leading-none mt-0.5">{format(mo.date, "yy")}</p>
                    <p className={`text-xl font-bold mt-1 leading-none ${mo.count > 0 ? "text-gray-900 dark:text-gray-100" : "text-gray-300 dark:text-gray-600"}`}>
                      {mo.count}
                    </p>
                    {mo.premium > 0 && (
                      <p className="text-xs text-green-600 font-medium mt-0.5 leading-none">{fmt$(mo.premium)}/mo</p>
                    )}
                    {mo.count > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="mt-2 w-full">
                            <div className="flex gap-px h-1.5 rounded-full overflow-hidden w-full">
                              {RENEWAL_STAGES.map(s => {
                                const c = mo.stageCount[s.key] || 0;
                                if (c === 0) return null;
                                return (
                                  <div
                                    key={s.key}
                                    className={`${STAGE_BAR_COLORS[s.key] || "bg-gray-300"} h-full`}
                                    style={{ width: `${(c / mo.count) * 100}%` }}
                                  />
                                );
                              })}
                            </div>
                            <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 mt-1">
                              {RENEWAL_STAGES.map(s => {
                                const c = mo.stageCount[s.key] || 0;
                                if (c === 0) return null;
                                return (
                                  <span key={s.key} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${s.color}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${STAGE_BAR_COLORS[s.key] || "bg-gray-300"}`} />
                                    {c}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs space-y-0.5">
                          {RENEWAL_STAGES.map(s => {
                            const c = mo.stageCount[s.key] || 0;
                            if (c === 0) return null;
                            return (
                              <p key={s.key} className={`${s.color} font-medium flex items-center gap-1`}>
                                <span className={`w-2 h-2 rounded-full ${STAGE_BAR_COLORS[s.key] || "bg-gray-300"}`} />
                                {c} {s.label}
                              </p>
                            );
                          })}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 pt-2.5 border-t">
                {RENEWAL_STAGES.map(s => (
                  <div key={s.key} className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${STAGE_BAR_COLORS[s.key] || "bg-gray-300"}`} />
                    <span className="text-xs text-gray-500">{s.label}</span>
                  </div>
                ))}
                {filterMonth !== "all" && (
                  <button
                    onClick={() => setFilterMonth("all")}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear month filter ×
                  </button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Filters + View Toggle */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-40">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            <Select value={filterUrgency} onValueChange={setFilterUrgency}>
              <SelectTrigger className="h-8 text-sm w-32">
                <SelectValue placeholder="Urgency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All urgency</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="planning">Planning</SelectItem>
                <SelectItem value="future">Future</SelectItem>
                <SelectItem value="no_date">No Date</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 text-sm w-32">
                <SelectValue placeholder="Plan type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(PLAN_TYPE_STYLES).filter(([k]) => k !== "medical").map(([t, s]) => (
                  <SelectItem key={t} value={t}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="h-8 text-sm w-36">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {monthOptions.map(mo => (
                  <SelectItem key={mo.value} value={mo.value}>{mo.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex border rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode("table")}
                className={`p-1.5 ${viewMode === "table" ? "bg-blue-50 text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("pipeline")}
                className={`p-1.5 ${viewMode === "pipeline" ? "bg-blue-50 text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* TABLE VIEW */}
          {viewMode === "table" && (
            <Card>
              <CardContent className="p-0">
                {filteredRenewals.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No renewals match your filters</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Coverage</TableHead>
                        <TableHead>Renewal Date</TableHead>
                        <TableHead>Urgency</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Open Enrollment</TableHead>
                        <TableHead>OE Progress</TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRenewals.map(r => {
                        const stage = getStage(r.clientId);
                        const isComplete = stage === "complete";
                        const urgCfg = URGENCY_CONFIG[r.urgency];
                        return (
                          <TableRow
                            key={r.clientId}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${isComplete ? "" : r.urgency === "overdue" ? "bg-red-100/60 dark:bg-red-950/30" : r.urgency === "critical" ? "bg-red-50/40 dark:bg-red-950/20" : ""}`}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {!isComplete && (r.urgency === "overdue" || r.urgency === "critical") && <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                                {largestRenewal?.clientId === r.clientId && <Star className="h-3.5 w-3.5 text-amber-400 shrink-0" />}
                                <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{r.clientName}</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">{r.planCount} plan{r.planCount !== 1 ? "s" : ""}</p>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {r.planTypes.map(t => <PlanTypeBadge key={t} type={t} />)}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                              {r.earliestRenewalDate ? format(r.earliestRenewalDate, "MMM d, yyyy") : <span className="text-gray-400 italic">—</span>}
                            </TableCell>
                            <TableCell>
                              <UrgencyBadge days={r.daysUntil} noDate={r.noDate} isComplete={isComplete} />
                            </TableCell>
                            <TableCell>
                              <Select value={stage} onValueChange={v => setStage(r.clientId, v)}>
                                <SelectTrigger className="h-7 text-xs w-[13rem] border border-gray-200 dark:border-gray-700 rounded px-2">
                                  <SelectValue>
                                    <StageBadge stageKey={stage} />
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {RENEWAL_STAGES.map(s => (
                                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <OEDateCell clientId={r.clientId} oeData={oeMap.get(r.clientId)} renewalDate={r.earliestRenewalDate} />
                            </TableCell>
                            <TableCell>
                              <OEProgressCell progress={oeProgressMap.get(r.clientId)} />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openRenewalNotice(r)}>
                                      <Mail className="h-3.5 w-3.5 text-gray-400" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Send renewal notice</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Link href={`/quoting?wizard=true&mode=rfp&step=1&client=${r.clientId}`}>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                        <FileText className="h-3.5 w-3.5 text-gray-400" />
                                      </Button>
                                    </Link>
                                  </TooltipTrigger>
                                  <TooltipContent>Start RFP</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Link href={`/clients/${r.clientId}`}>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                        <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                                      </Button>
                                    </Link>
                                  </TooltipTrigger>
                                  <TooltipContent>View client</TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {/* PIPELINE VIEW */}
          {viewMode === "pipeline" && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {(["overdue", "critical", "urgent", "planning", "future"] as UrgencyLevel[]).map(level => {
                const cfg = URGENCY_CONFIG[level];
                const items = filteredRenewals.filter(r => r.urgency === level);
                return (
                  <div key={level} className={`rounded-xl border ${cfg.border} ${cfg.bg} dark:bg-opacity-10 p-3`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                        <span className={`text-xs font-bold uppercase tracking-wide ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-500 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full border">
                        {items.length}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {items.length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-4">None</p>
                      )}
                      {items.map(r => (
                        <div key={r.clientId} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                          <div className="flex items-start justify-between gap-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">{r.clientName}</p>
                            <Link href={`/clients/${r.clientId}`}>
                              <ExternalLink className="h-3.5 w-3.5 text-gray-300 hover:text-blue-500 shrink-0 mt-0.5" />
                            </Link>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{r.earliestRenewalDate ? format(r.earliestRenewalDate, "MMM d, yyyy") : "No date"}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {r.planTypes.map(t => <PlanTypeBadge key={t} type={t} />)}
                          </div>
                          {r.totalMonthlyPremium > 0 && (
                            <p className="text-xs text-green-600 font-medium mt-2">{fmt$(r.totalMonthlyPremium)}/mo premium</p>
                          )}
                          <div className="mt-2">
                            <Select value={getStage(r.clientId)} onValueChange={v => setStage(r.clientId, v)}>
                              <SelectTrigger className="h-6 text-xs shadow-none border-gray-200 w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {RENEWAL_STAGES.map(s => (
                                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── OPPORTUNITIES TAB ── */}
        <TabsContent value="opportunities" className="mt-0 space-y-4">

          {/* ── KPI Tiles (gradient + icon, distinct from Renewals KPI cards) ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-950/40 dark:to-orange-900/30 border border-amber-200/60 dark:border-amber-800/40 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">Coverage Gaps</p>
                  <p className="text-3xl font-bold text-amber-900 dark:text-amber-100 mt-1 leading-none">
                    {opportunities.reduce((s, o) => s + o.missingTypes.length, 0)}
                  </p>
                  <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mt-1.5">missing core lines across clients</p>
                </div>
                <div className="shrink-0 rounded-full bg-amber-200/70 dark:bg-amber-800/40 p-2">
                  <TrendingUp className="h-5 w-5 text-amber-700 dark:text-amber-300" />
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-rose-50 to-pink-100 dark:from-rose-950/40 dark:to-pink-900/30 border border-rose-200/60 dark:border-rose-800/40 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-rose-700 dark:text-rose-300 uppercase tracking-wider">Clients with Gaps</p>
                  <p className="text-3xl font-bold text-rose-900 dark:text-rose-100 mt-1 leading-none">{opportunities.length}</p>
                  <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-1.5">missing ≥1 core line</p>
                </div>
                <div className="shrink-0 rounded-full bg-rose-200/70 dark:bg-rose-800/40 p-2">
                  <AlertCircle className="h-5 w-5 text-rose-700 dark:text-rose-300" />
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-950/40 dark:to-green-900/30 border border-emerald-200/60 dark:border-emerald-800/40 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">Quick Wins</p>
                  <p className="text-3xl font-bold text-emerald-900 dark:text-emerald-100 mt-1 leading-none">{quickWins.length}</p>
                  <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80 mt-1.5">clients missing exactly 1 line</p>
                </div>
                <div className="shrink-0 rounded-full bg-emerald-200/70 dark:bg-emerald-800/40 p-2">
                  <Sparkles className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-950/40 dark:to-purple-900/30 border border-violet-200/60 dark:border-violet-800/40 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wider">Most Common Gap</p>
                  <p className="text-3xl font-bold text-violet-900 dark:text-violet-100 mt-1 leading-none">
                    {crossSellByType[0] ? (PLAN_TYPE_STYLES[crossSellByType[0].type]?.label || crossSellByType[0].type) : "—"}
                  </p>
                  <p className="text-xs text-violet-700/80 dark:text-violet-300/80 mt-1.5">
                    {crossSellByType[0] ? `${crossSellByType[0].count} client${crossSellByType[0].count !== 1 ? "s" : ""} missing it` : "no gaps"}
                  </p>
                </div>
                <div className="shrink-0 rounded-full bg-violet-200/70 dark:bg-violet-800/40 p-2">
                  <Target className="h-5 w-5 text-violet-700 dark:text-violet-300" />
                </div>
              </div>
            </div>
          </div>

          {opportunities.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle2 className="h-10 w-10 text-green-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">All clients have comprehensive coverage — great work!</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* ── Analytics Row ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* Gap by Coverage Type */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-amber-500" />
                      Coverage Gaps by Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {crossSellByType.map(({ type, count }) => {
                        const key = type === "medical" ? "health" : type;
                        const style = PLAN_TYPE_STYLES[key] ?? { bg: "bg-gray-100", text: "text-gray-700", label: type, dot: "bg-gray-400" };
                        const maxCount = crossSellByType[0]?.count || 1;
                        const pct = Math.round((count / maxCount) * 100);
                        return (
                          <div key={type} className="space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                                  {style.label}
                                </span>
                              </div>
                              <span className="text-xs font-semibold text-amber-600 tabular-nums">{count} client{count !== 1 ? "s" : ""} missing</span>
                            </div>
                            <div className="bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                              <div className={`h-full rounded-full ${style.dot}`} style={{ width: `${Math.max(pct, 4)}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Coverage Completeness */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Layers className="h-4 w-4 text-blue-500" />
                      Coverage Completeness
                      <Tooltip>
                        <TooltipTrigger>
                          <ShieldAlert className="h-3.5 w-3.5 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          How many of the {CORE_COVERAGE_TYPES.length} core lines ({CORE_COVERAGE_LABELS}) each client has. Clients with fewer lines have bigger gaps and more revenue potential.
                        </TooltipContent>
                      </Tooltip>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {coverageCompleteness.map(({ lines, label, count }) => {
                        const maxCount = Math.max(...coverageCompleteness.map(x => x.count), 1);
                        const pct = Math.round((count / maxCount) * 100);
                        const barColor = lines === 1 ? "bg-red-400" : lines === 2 ? "bg-amber-400" : "bg-blue-400";
                        const textColor = lines === 1 ? "text-red-600" : lines === 2 ? "text-amber-600" : "text-blue-600";
                        const labelSuffix = lines === 1 ? " — high gap" : lines === 2 ? " — moderate gap" : " — near complete";
                        if (count === 0) return null;
                        return (
                          <div key={lines} className="space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-semibold tabular-nums ${textColor}`}>{label} lines</span>
                                <span className="text-xs text-gray-400">{labelSuffix}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{count} client{count !== 1 ? "s" : ""}</span>
                              </div>
                            </div>
                            <div className="bg-gray-100 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.max(pct, 4)}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {quickWins.length > 0 && (
                        <p className="text-xs text-green-600 flex items-center gap-1 pt-1 border-t">
                          <CheckCircle2 className="h-3 w-3" />
                          {quickWins.length} quick win{quickWins.length !== 1 ? "s" : ""} — clients missing exactly 1 line
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* ── Client Table ── */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-500" />
                    All Cross-Sell Opportunities
                    <span className="ml-auto text-xs font-normal text-gray-400">quick wins first, then largest clients</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Has Coverage</TableHead>
                        <TableHead>Missing Lines</TableHead>
                        <TableHead className="text-right">Current Premium/mo</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {opportunities.map(opp => {
                        const isQuickWin = opp.missingTypes.length === 1;
                        return (
                          <TableRow key={opp.clientId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {isQuickWin && (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700 border border-green-200 shrink-0">
                                        ★
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>Quick win — missing only 1 line</TooltipContent>
                                  </Tooltip>
                                )}
                                <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{opp.clientName}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {opp.hasTypes.map(t => <PlanTypeBadge key={t} type={t} />)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {opp.missingTypes.map(type => {
                                  const key = type === "medical" ? "health" : type;
                                  const style = PLAN_TYPE_STYLES[key];
                                  return (
                                    <span key={type} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                      <Zap className="h-3 w-3" />
                                      {style?.label || type}
                                    </span>
                                  );
                                })}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-sm text-gray-600 dark:text-gray-400 font-medium">
                              {opp.currentMonthlyPremium > 0 ? `$${Math.round(opp.currentMonthlyPremium).toLocaleString()}` : "—"}
                            </TableCell>
                            <TableCell>
                              <Link href={`/clients/${opp.clientId}`}>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ── ANALYTICS TAB ── */}
        <TabsContent value="analytics" className="mt-0 space-y-4">

          {/* ── ROW 1: Renewal Pipeline by Stage (full width) ── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4 text-blue-500" />
                Renewal Pipeline by Stage
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 text-xs font-normal text-gray-500 border border-gray-200 dark:border-gray-700 rounded-full px-2 py-0.5 cursor-help">
                      <AlertCircle className="h-3 w-3" />
                      Active pipeline only (next 90 days)
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    This chart intentionally excludes renewals more than 90 days out. Long-lead renewals haven't entered the active work cycle yet, so including them would inflate the "Not Started" bar with non-actionable noise. The footer shows the exact count being displayed vs. the full 12-month book.
                  </TooltipContent>
                </Tooltip>
                {notStartedAtRisk.length > 0 && (
                  <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold border border-red-200">
                    <AlertTriangle className="h-3 w-3" />
                    {notStartedAtRisk.length} not started — due in 60d
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activePipeline.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No renewals in the next 90 days</p>
              ) : (
                <div className="space-y-2.5">
                  {stageDistribution.map(s => {
                    const maxCount = Math.max(...stageDistribution.map(x => x.count), 1);
                    const pct = Math.round((s.count / maxCount) * 100);
                    return (
                      <div key={s.key} className="flex items-center gap-3">
                        <span className={`text-xs font-medium w-36 shrink-0 ${s.color}`}>{s.label}</span>
                        <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-5 overflow-hidden">
                          {s.count > 0 && (
                            <div
                              className={`h-full rounded-full flex items-center pl-2 transition-all ${s.bg} border ${s.border}`}
                              style={{ width: `${Math.max(pct, 3)}%` }}
                            >
                              {pct > 20 && (
                                <span className={`text-xs font-semibold ${s.color}`}>
                                  {s.count} client{s.count !== 1 ? "s" : ""}
                                  {s.premium > 0 && ` · ${fmt$(s.premium)}/mo`}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {(pct <= 20 || s.count === 0) && (
                          <span className="text-xs text-gray-400 w-28 text-right">
                            {s.count === 0 ? "—" : `${s.count} client${s.count !== 1 ? "s" : ""}${s.premium > 0 ? ` · ${fmt$(s.premium)}/mo` : ""}`}
                          </span>
                        )}
                        {pct > 20 && (
                          <span className="text-xs text-gray-400 w-28 text-right" />
                        )}
                      </div>
                    );
                  })}
                  <p className="text-xs text-gray-400 pt-2 border-t">
                    Showing {activePipeline.length} of {allRenewals.length} renewals — only those due within 90 days. Stage assigned per renewal on the Renewals tab.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── ROW 2: Premium at Risk | Renewals by Month ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Premium at Risk by Urgency */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-500" />
                  Premium at Risk by Urgency
                </CardTitle>
              </CardHeader>
              <CardContent>
                {allRenewals.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No renewal data</p>
                ) : (
                  <div className="space-y-3">
                    {/* Stacked bar showing premium distribution */}
                    <div className="flex h-4 rounded-full overflow-hidden gap-px">
                      {premiumByUrgency.filter(u => u.premium > 0).map(u => (
                        <Tooltip key={u.urgency}>
                          <TooltipTrigger asChild>
                            <div
                              className={`h-full ${u.bar} transition-all cursor-default`}
                              style={{ width: `${u.pct}%` }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>{u.label}: {fmt$(u.premium)}/mo ({u.pct}%)</TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                    {/* Tier breakdown */}
                    <div className="space-y-2 mt-1">
                      {premiumByUrgency.map(u => (
                        <div key={u.urgency} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${u.bar}`} />
                            <span className="text-xs text-gray-600 dark:text-gray-400">{u.label}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-400">{u.count} client{u.count !== 1 ? "s" : ""}</span>
                            <span className={`text-xs font-semibold tabular-nums w-16 text-right ${u.count > 0 ? u.text : "text-gray-300"}`}>
                              {u.premium > 0 ? `${fmt$(u.premium)}/mo` : "—"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="pt-2 border-t flex justify-between text-xs">
                      <span className="text-gray-400">Total monthly premium (12-mo book)</span>
                      <span className="font-bold text-green-600">
                        {fmt$(totalBookMonthlyPremium)}/mo
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Renewals by Month */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart2 className="h-4 w-4 text-green-500" />
                  {calendarData.some(mo => mo.premium > 0)
                    ? "Premium by Renewal Month"
                    : "Clients Renewing by Month"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {calendarData.every(mo => mo.count === 0) ? (
                  <p className="text-sm text-gray-400 text-center py-6">No upcoming renewals in the next 12 months</p>
                ) : (() => {
                  const hasPremium = calendarData.some(mo => mo.premium > 0);
                  const maxVal = hasPremium
                    ? Math.max(...calendarData.map(m => m.premium), 1)
                    : Math.max(...calendarData.map(m => m.count), 1);
                  return (
                    <div className="space-y-2">
                      {calendarData.filter(mo => mo.count > 0).map(mo => {
                        const val = hasPremium ? mo.premium : mo.count;
                        const pct = Math.round((val / maxVal) * 100);
                        return (
                          <div key={mo.value} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-10 shrink-0">{format(mo.date, "MMM")}</span>
                            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-5 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full flex items-center pl-2 transition-all"
                                style={{ width: `${Math.max(pct, 4)}%` }}
                              >
                                {pct > 25 && (
                                  <span className="text-xs text-white font-medium">
                                    {hasPremium ? `${fmt$(mo.premium)}/mo` : `${mo.count} client${mo.count !== 1 ? "s" : ""}`}
                                  </span>
                                )}
                              </div>
                            </div>
                            {pct <= 25 && (
                              <span className="text-xs text-gray-500 w-16 text-right">
                                {hasPremium ? `${fmt$(mo.premium)}/mo` : `${mo.count} client${mo.count !== 1 ? "s" : ""}`}
                              </span>
                            )}
                            {pct > 25 && (
                              <span className="text-xs text-gray-400 w-16 text-right">{mo.count} client{mo.count !== 1 ? "s" : ""}</span>
                            )}
                          </div>
                        );
                      })}
                      {!hasPremium && (
                        <p className="text-xs text-gray-400 mt-3 pt-3 border-t">
                          Add monthly premium to client plans to see premium totals
                        </p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* ── ROW 3: Cross-sell Opportunity | Carrier Concentration ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Cross-sell Opportunity Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-amber-500" />
                  Cross-sell Opportunity by Coverage Gap
                  {opportunities.length > 0 && (
                    <span className="ml-auto text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      {opportunities.length} client{opportunities.length !== 1 ? "s" : ""} with gaps
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {crossSellByType.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No coverage gaps identified</p>
                ) : (
                  <div className="space-y-3">
                    {crossSellByType.map(({ type, count }) => {
                      const key = type === "medical" ? "health" : type;
                      const style = PLAN_TYPE_STYLES[key] ?? { bg: "bg-gray-100", text: "text-gray-700", label: type, dot: "bg-gray-400" };
                      const maxCount = crossSellByType[0].count || 1;
                      const pct = Math.round((count / maxCount) * 100);
                      return (
                        <div key={type} className="space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                                {style.label}
                              </span>
                            </div>
                            <span className="text-xs font-semibold text-amber-600 tabular-nums">{count} client{count !== 1 ? "s" : ""} missing</span>
                          </div>
                          <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div className={`h-full rounded-full ${style.dot}`} style={{ width: `${Math.max(pct, 4)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-gray-400 pt-2 border-t">
                      See Opportunities tab for the client list
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Carrier Concentration */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Layers className="h-4 w-4 text-indigo-500" />
                  Carrier Concentration
                  <Tooltip>
                    <TooltipTrigger>
                      <ShieldAlert className="h-3.5 w-3.5 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      High concentration with a single carrier is a risk. If that carrier takes aggressive rate action, a large portion of your book is exposed.
                    </TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {carrierConcentration.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No carrier data available</p>
                ) : (
                  <div className="space-y-3">
                    {carrierConcentration.map((c, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 dark:text-gray-400 w-28 truncate shrink-0">{c.name}</span>
                        <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${c.pct > 50 ? "bg-red-400" : c.pct > 30 ? "bg-amber-400" : "bg-indigo-400"}`}
                            style={{ width: `${Math.max(c.pct, 3)}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-10 text-right">{c.pct}%</span>
                      </div>
                    ))}
                    {carrierConcentration[0]?.pct > 50 && (
                      <p className="text-xs text-red-500 flex items-center gap-1 mt-2">
                        <AlertTriangle className="h-3 w-3" />
                        High concentration risk — consider diversifying across carriers
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── ROW 4: Book Coverage Mix | Top Renewals ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Coverage Mix */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Layers className="h-4 w-4 text-blue-500" />
                  Book Coverage Mix
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const typeCounts: Record<string, number> = {};
                  allRenewals.forEach(r => r.planTypes.forEach(t => { typeCounts[t] = (typeCounts[t] || 0) + 1; }));
                  const total = allRenewals.length || 1;
                  const entries = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);
                  if (entries.length === 0) return <p className="text-sm text-gray-400 text-center py-6">No data</p>;
                  return (
                    <div className="space-y-2">
                      {entries.map(([type, count]) => {
                        const key = type === "medical" ? "health" : type;
                        const style = PLAN_TYPE_STYLES[key];
                        const pct = Math.round((count / total) * 100);
                        return (
                          <div key={type} className="flex items-center gap-3">
                            <span className="text-xs w-20 shrink-0">
                              <PlanTypeBadge type={type} />
                            </span>
                            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                              <div className={`h-full rounded-full ${style?.dot || "bg-gray-400"}`} style={{ width: `${Math.max(pct, 3)}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 w-16 text-right">{count} client{count !== 1 ? "s" : ""} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Top Renewals by Revenue */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-400" />
                  Top Renewals by Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                {allRenewals.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No renewal data</p>
                ) : (
                  <div className="space-y-3">
                    {[...allRenewals]
                      .sort((a, b) => b.totalMonthlyPremium - a.totalMonthlyPremium)
                      .slice(0, 5)
                      .map((r, i) => (
                        <div key={r.clientId} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.clientName}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-xs text-gray-400">{r.earliestRenewalDate ? format(r.earliestRenewalDate, "MMM d, yyyy") : "No date"}</span>
                                <span className="text-gray-300">·</span>
                                {/* Same rule as the Renewals table: a completed
                                    stage means the plan already renewed, so no
                                    overdue/critical alarm — show "Renewed". */}
                                <UrgencyBadge
                                  days={r.daysUntil}
                                  noDate={r.noDate}
                                  isComplete={getStage(r.clientId) === "complete"}
                                />
                                <span className="text-gray-300">·</span>
                                <StageBadge stageKey={getStage(r.clientId)} />
                              </div>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-green-600">{fmt$(r.totalMonthlyPremium)}/mo</p>
                            <Link href={`/clients/${r.clientId}`}>
                              <Button variant="ghost" size="sm" className="h-6 text-xs p-1 text-gray-400 hover:text-blue-500">
                                View <ChevronRight className="h-3 w-3 ml-0.5" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

        </TabsContent>
      </Tabs>

      <Dialog open={!!noticeTarget} onOpenChange={(open) => { if (!open) setNoticeTarget(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send renewal notice</DialogTitle>
            <DialogDescription>
              {noticeRecipient ? (
                <>Emails <span className="font-medium">{noticeRecipient}</span> and notifies the employer portal. Replies go to your email.</>
              ) : (
                <span className="text-red-600">This client has no contact email on file — add one on the client profile first.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Subject</label>
              <Input
                value={noticeSubject}
                onChange={e => setNoticeSubject(e.target.value)}
                maxLength={200}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Message</label>
              <Textarea
                value={noticeMessage}
                onChange={e => setNoticeMessage(e.target.value)}
                rows={8}
                maxLength={5000}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNoticeTarget(null)} disabled={noticeSending}>
              Cancel
            </Button>
            <Button
              onClick={handleSendNotice}
              disabled={noticeSending || !noticeRecipient || !noticeSubject.trim() || !noticeMessage.trim()}
            >
              {noticeSending ? "Sending…" : "Send notice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
